/* CRO test exposure helper.
 *
 * Single source of truth for the 4-part attribution contract documented in
 * Doc/CRO/CRO_Test_Launch_Standard.md:
 *
 *   1. Shopify cart attribute (cro_test_<id>=<arm>) — read by the generic
 *      Custom Pixel to fire cro_purchase on checkout_completed.
 *   2. dataLayer.push({event:'ab_test_exposure', ...}) — UNCONDITIONAL.
 *      Survives the gtag race (when GTM hasn't yet defined window.gtag).
 *   3. gtag('set') + gtag('event','ab_test_exposure') with retry. Belt for
 *      the suspenders — when gtag IS loaded, GA4 receives it directly so
 *      event-scoped cl_test_id/cl_test_variant dims propagate to all
 *      subsequent events on this page (page_view, view_item, etc.).
 *   4. window.stix.track.exposeTest — fans out to Klaviyo profile + TW
 *      journey via stix-track.js.
 *
 * Use:
 *   window.stixCroExpose(TEST_ID, arm, { page_type: 'pdp' });
 *
 * Idempotent per (testId, arm) tuple within a single page load. Cart write
 * is fire-and-forget with keepalive:true so it survives the unload that a
 * fast clicker may trigger before the response returns.
 *
 * History: 2026-05-13 — added because TEST-005..009 launched with only an
 * `if(typeof gtag==='function')` check (no retry, no dataLayer fallback),
 * which silently dropped exposure events when gtag hadn't loaded yet. The
 * cart-attr write happened inside the same fire() function and was missed
 * for any test whose apply() threw before fire() ran. This helper makes the
 * three independent emissions truly independent.
 */
(function () {
  'use strict';
  if (window.stixCroExpose) return;

  var _seen = Object.create(null);

  function pushDataLayer(testId, arm, opts) {
    try {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'ab_test_exposure',
        cl_test_id: testId,
        cl_test_variant: arm,
        page_type: (opts && opts.page_type) || 'pdp',
        page_path: window.location.pathname,
        assignment_source: (opts && opts.assignment_source) || undefined
      });
    } catch (e) { /* never break the page */ }
  }

  function fireGtag(testId, arm, opts) {
    try {
      window.gtag('set', { cl_test_id: testId, cl_test_variant: arm });
      window.gtag('event', 'ab_test_exposure', {
        cl_test_id: testId,
        cl_test_variant: arm,
        page_type: (opts && opts.page_type) || 'pdp',
        page_path: window.location.pathname
      });
      return true;
    } catch (e) { return false; }
  }

  function writeCartAttr(testId, arm) {
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'attributes[cro_test_' + testId + ']=' + encodeURIComponent(arm),
        credentials: 'same-origin',
        keepalive: true
      }).catch(function () { /* silent */ });
    } catch (e) { /* silent */ }
  }

  function fireStixTrack(testId, arm) {
    try {
      if (window.stix && window.stix.track && window.stix.track.exposeTest) {
        window.stix.track.exposeTest(testId, arm, { page_path: window.location.pathname });
      }
    } catch (e) { /* silent */ }
  }

  window.stixCroExpose = function (testId, arm, opts) {
    if (!testId || !arm) return;
    var key = testId + '|' + arm;
    if (_seen[key]) return;
    _seen[key] = true;
    opts = opts || {};

    // (1) Cart attribute — must happen even if gtag, dataLayer, or stix.track
    //     are unavailable. This is the channel that survives storefront →
    //     checkout and feeds the Custom Pixel cro_purchase event.
    writeCartAttr(testId, arm);

    // (4) Klaviyo / TW exposure — independent of GA4.
    fireStixTrack(testId, arm);

    // (2) dataLayer push — UNCONDITIONAL. GTM containers reading
    //     ab_test_exposure will pick this up even if gtag is missing.
    pushDataLayer(testId, arm, opts);

    // (3) gtag — try immediately, then retry every 100ms for up to 5s while
    //     waiting for GTM to define window.gtag. Mirrors cro-shadow.liquid.
    if (typeof window.gtag === 'function' && fireGtag(testId, arm, opts)) return;
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      if (typeof window.gtag === 'function') {
        fireGtag(testId, arm, opts);
        clearInterval(iv);
      } else if (attempts > 50) {
        clearInterval(iv);
      }
    }, 100);
  };
})();
